import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Client, Clients, ClientsSearch } from '../models/client';
import {
  BehaviorSubject,
  tap,
  Subject,
  catchError,
  of,
  Observable,
} from 'rxjs';
import { PersistenceService } from '@migo-apps/utils';
import { additionalField } from '../../new-link/models/additional-fields.model';

const generateEndpoint = environment.generateEndpoint;

@Injectable({
  providedIn: 'root',
})
export class ClientsService {
  private _clients = new BehaviorSubject<Client[] | undefined>(undefined);
  clients$ = this._clients.asObservable();

  private _selectedClient = new BehaviorSubject<Client | null | undefined>(
    undefined
  );
  selectedClient$ = this._selectedClient.asObservable();
  get selectedClient() {
    return this._selectedClient.getValue();
  }

  private _selectionChange = new Subject();
  selectionChange$ = this._selectionChange.asObservable();

  private _selectedClientKey = 'selected_client';

  /**
   * We're checking to see if there's a client in local storage. If there is, we're
   * selecting it
   * @param {HttpClient} http - HttpClient - This is the Angular HttpClient service.
   * @param {PersistenceService} persistenceSvc - PersistenceService - this is the
   * service that will be used to store the client data in the browser's local
   * storage.
   */
  constructor(
    private http: HttpClient,
    private persistenceSvc: PersistenceService
  ) {
    const selected = this.getLocalClient();

    if (selected && Object.keys(selected).length > 0) {
      this.selectClient(selected);
    }
  }

  /**
   * It gets the clients for a user, then sets the clients in the store, and if
   * there's no selected client, it selects the first client in the list
   * @param {number} userId - number - The user ID of the user you want to get the
   * clients for.
   * @returns An observable of the clients.
   */
  getClients(userId: number) {
    return this.http
      .get<Clients>(generateEndpoint(['accounts', userId, 'clients']))
      .pipe(
        tap((data) => {
          this._clients.next(data.items);
          const selected = this.getSelectedClient();

          if (!selected || Object.keys(selected).length === 0) {
            this.selectClient(data.items[0]);
          } else {
            const fresh = data.items.find((c) => c.key === selected.key || c.id === selected.id);
            if (fresh) this.selectClient(fresh);
          }
        }),
        catchError((err) => {
          this.selectClient(null);
          throw err;
        })
      );
  }

  getAllClientsBySearch(query: string) {
    return this.http
      .get<ClientsSearch>(generateEndpoint(['users/clients-list']) + query)
      .pipe(
        catchError((err) => {
          return of(undefined);
        })
      );
  }

  /**
   * It takes a client object, converts it to a compatible client object, and then
   * saves it to local storage
   * @param {Client | null} client - Client | null
   */
  selectClient(client: Client | null) {
    let compatibleClient: Client | null = null;

    if (client) {
      compatibleClient = {
        ...client,
        key: client?.key || client?.name || '' || null,
        id: client?.id || client?.clientId || '' || null,
      } as Client;
    }

    this.persistenceSvc.local.setItem(
      this._selectedClientKey,
      compatibleClient
    );
    this._selectedClient.next(compatibleClient);
    this._selectionChange.next(null);
  }

  /**
   * > Get the client from local storage
   * @returns The local client.
   */
  private getLocalClient() {
    return this.persistenceSvc.local.getItem<Client>(this._selectedClientKey);
  }

  /**
   * This function returns the value of the private _selectedClient variable.
   * @returns The value of the private variable _selectedClient.
   */
  private getSelectedClient() {
    return this._selectedClient.getValue();
  }

  /**
   * Retrieves the current list of clients stored in the service.
   * This function returns the latest value of the `_clients` BehaviorSubject
   * without requiring an observable subscription.
   *
   * @returns {Client[] | undefined} The current list of clients or `undefined` if not set.
   */
  getClientsValue(): Client[] | undefined {
    return this._clients.getValue();
  }

  getAdditionalFields(key: string): Observable<additionalField[]> {
    return this.http.get<additionalField[]>(
      generateEndpoint(['clients', key, 'additionalFields'])
    );
  }

  createAdditionalField(
    key: string,
    additional: additionalField
  ): Observable<any> {
    return this.http.post<any>(
      generateEndpoint(['clients', key, 'additionalFields']),
      additional
    );
  }

  updateAdditionalField(
    key: string,
    fieldName: string,
    additional: additionalField
  ): Observable<any> {
    return this.http.put<any>(
      generateEndpoint(['clients', key, 'additionalFields', fieldName]),
      additional
    );
  }

  deleteField(key: string, fieldName: string) {
    return this.http.delete(
      generateEndpoint(['clients', key, 'additionalFields', fieldName])
    );
  }
}
